/**
 * Array containing accepted file types for file input elements.
 * @constant {Array<string>} acceptTypeFiles - Array of accepted file types.
 */
export const acceptTypeFiles = [
  "application/vnd.oasis.opendocument.graphics",
  "image/*",
  "audio/*",
  "video/mp3",
  "video/mp4",
  "video/mpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "application/x-zip-compressed",
  "application/vsd.ms-outlook",
  "application/pkcs7-mime",
  "text/plain",
  "text/csv",
  "message/rfc822",
  "application/vnd.ms-outlook",
  "text/xml",
  "application/xml",
];

/**
 * Array containing accepted file types for file input elements.
 * @constant {Array<string>} acceptTypeFiles - Array of accepted file types.
 */
export const acceptTypeFilesObject = {
  "application/vnd.oasis.opendocument.graphics": "ppt",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "jpg",
  "image/gif": "jpg",
  "image/webp": "jpg",
  "image/bmp": "jpg",
  "image/tiff": "jpg",
  "image/*": "jpg",
  "audio/mpeg": "mp4",
  "audio/mp3": "mp4",
  "audio/wav": "mp4",
  "audio/ogg": "mp4",
  "audio/*": "mp4",
  "video/mp4": "mp4",
  "video/mpeg": "mp4",
  "video/x-msvideo": "mp4",
  "video/quicktime": "mp4",
  "video/*": "mp4",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.oasis.opendocument.text": "docx",
  "application/vnd.oasis.opendocument.spreadsheet": "pptx",
  "application/vnd.oasis.opendocument.presentation": "pptx",
  "application/x-zip-compressed": "zip",
  "application/zip": "zip",
  "application/x-rar-compressed": "zip",
  "application/x-7z-compressed": "zip",
  "application/x-tar": "zip",
  "application/vsd.ms-outlook": "txt",
  "application/pkcs7-mime": "txt",
  "text/plain": "txt",
  "text/csv": "xlsx",
  "text/html": "txt",
  "text/javascript": "txt",
  "text/css": "txt",
  "message/rfc822": "eml",
  "application/vnd.ms-outlook": "txt",
  "application/json": "txt",
  "application/xml": "xml",
  "application/rtf": "txt",
  "text/xml": "xml",
  "application/x-pkcs12": "pfx",
};

/**
 * Retrieves the file extension corresponding to a given MIME type.
 * This function uses the `acceptTypeFilesObject` mapping to return the preferred
 * file extension for a specified content type (MIME type). If the MIME type is not
 * found in the mapping, it returns `null`.
 * @param {string} contentType - The MIME type of the file (e.g., "image/jpeg", "application/pdf").
 * @returns {string|null} The corresponding file extension (e.g., "jpg", "pdf") or `null` if the MIME type is not found.
 */
export function getFileExtensionFromContentType(contentType) {
  return acceptTypeFilesObject[contentType] || null;
}

/**
 * Checks if a file with the given MIME type is accepted for logo upload.
 * @param {string} fileType - The MIME type of the file to be checked.
 * @returns {boolean} - Returns true if the file type is accepted for logo upload, otherwise false.
 */
export const acceptedFilesLogo = ["image/jpeg", "image/png", "image/jpg"];

/**
 * An object mapping MIME types to their corresponding file type abbreviations.
 * This is used to identify and categorize accepted file types for logos.
 */
export const acceptedFilesLogoObject = {
  "application/vnd.oasis.opendocument.graphics": "ppt",
  "image/jpeg": "jpeg",
  "image/jpg": "jpg",
  "image/png": "png",
};

/**
 * Checks if a given filename matches the accepted file types for logos.
 * @param {string} filename - The name of the file to be checked.
 * @returns {boolean} Returns true if the filename matches the accepted file types, otherwise false.
 */
export const acceptFileLogoRegex = new RegExp(
  `^(${acceptedFilesLogo.join("|").replace(/\*/g, ".+")})$`,
);

/**
 * Regular expression pattern for matching file names based on accepted file types.
 * @constant {RegExp} acceptFilesRegex
 * @param {string[]} acceptTypeFiles - Array containing accepted file types.
 * @returns {RegExp} Regular expression pattern generated based on the accepted file types.
 */
export const acceptFilesRegex = new RegExp(`^(${acceptTypeFiles.join("|").replace(/\*/g, ".+")})$`);

/**
 * An array of MIME types representing accepted file formats.
 * This template includes:
 * - Microsoft Word Open XML Document (.docx)
 */
export const acceptedFilesTemplate = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/**
 * An object mapping MIME types to their corresponding file extensions.
 * @property {string} application/vnd.openxmlformats-officedocument.wordprocessingml.document - Represents the file extension for Microsoft Word Open XML documents (.docx).
 */
export const acceptedFilesTemplateObject = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

/**
 * A regular expression pattern that matches file types specified in the
 * acceptedFilesTemplate array. The pattern is constructed by joining the
 * MIME types with a pipe '|' character and replacing any asterisks '*' with
 * '.+' to allow for wildcard matching.
 */
export const acceptFilesTemplateRegex = new RegExp(
  `^(${acceptedFilesTemplate.join("|").replace(/\*/g, ".+")})$`,
);

/**
 * An object defining accepted file types for PFX files used in digital signatures.
 * The object maps the MIME type "application/x-pkcs12" to an array containing the
 * file extension ".pfx".
 * @constant {Object} acceptedFilesPfxObject - Object defining accepted PFX file types.
 */
export const acceptedFilesPfxObject = {
  "application/x-pkcs12": [".pfx"],
};

/**
 * An array of MIME types representing accepted PFX file formats.
 * This array includes the MIME type for PKCS #12 files.
 */
export const acceptedFilesPfx = ["application/x-pkcs12"];

/** A regular expression pattern that matches PFX file types specified in the
 * acceptedFilesPfx array. The pattern is constructed by joining the
 * MIME types with a pipe '|' character and replacing any asterisks '*' with
 * '.+' to allow for wildcard matching.
 */
export const acceptFilesPfxRegex = new RegExp(
  `^(${acceptedFilesPfx.join("|").replace(/\*/g, ".+")})$`,
);
